import { ComponentPropsWithoutRef, Ref, forwardRef } from "react";
import { useTrackingId } from "../../hooks";
import { DataTrackingId, LayoutUtilProps } from "../../types";
import { childrenToString } from "../../internal/functions";
import { useLayoutPropsUtil } from "../../internal/hooks";

import cx from "classnames";
import styles from "../Button/Button.module.scss";

/**
 * Props for the ButtonCompound component
 * @extends LayoutUtilProps
 * @extends ComponentPropsWithoutRef<"button">
 */
export type ButtonCompoundProps = LayoutUtilProps &
  ComponentPropsWithoutRef<"button"> & {
    /**
     * The shape of the compound button.
     * @default rounded
     */
    shape?: "pill" | "circular" | "rounded";
  } &
  /**
   * Data tracking id
   */
  DataTrackingId;

/**
 * ButtonCompound component for creating compound button layouts.
 *
 * Features:
 * - Three shape options (pill, circular, rounded)
 * - Transparent background for compound layouts
 * - Supports layout utilities for positioning and spacing
 * - Full accessibility support with proper ARIA attributes
 * - Flexible content support
 * - Consistent styling with other button components
 * - Hover and active state management
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <ButtonCompound shape="circular">
 *   <Avatar name="John Doe" />
 * </ButtonCompound>
 */
export const ButtonCompound = forwardRef(
  (props: ButtonCompoundProps, ref: Ref<HTMLButtonElement>) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      shape = "rounded",
      type,
      disabled,
      children,
      style,
      ...rest
    } = componentProps;

    const data = {
      children: childrenToString(props.children),
      shape,
      type,
    };

    const trackingId = useTrackingId({
      name: "ButtonCompound",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const buttonClassNames = cx(
      className,
      styles["button"],
      styles["button-compound"],
      {
        [styles["shape-pill"]]: shape === "pill",
        [styles["shape-circular"]]: shape === "circular",
        [styles["shape-rounded"]]: shape === "rounded",
      },
    );

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <button
        data-tracking-id={trackingId}
        className={buttonClassNames}
        type={type}
        disabled={disabled}
        data-anv="button-compound"
        ref={ref}
        style={styleCombined}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

ButtonCompound.displayName = "ButtonCompound";
